/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';

import {
	gutterStyles,
	HeroHeading,
	StatusPanel,
	type StatusPanelProps,
} from '@af/design-system-docs-ui';
import { Stack, Text } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/responsive';
// eslint-disable-next-line import/no-extraneous-dependencies
import { N10 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import ThemedImg from '../../../components/themed-img';

type HeroProps = {
	headline: string;
	description?: string;
	status?: StatusPanelProps;
	className?: string;
	imageSrc?: {
		light: string;
		dark: string;
	};
	imageDimensions?: {
		width: number | string;
		height: number | string;
	};
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	backgroundColor: token('color.background.neutral', N10),

	[media.above.sm]: {
		display: 'grid',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Content = styled.div({
	paddingBlock: token('space.500', '40px'),
	maxWidth: '50rem',

	[media.above.sm]: {
		paddingBlock: token('space.600', '48px'),
	},

	[media.above.md]: {
		paddingBlock: token('space.1000', '80px'),
	},
});

const heroImageStyles = css({
	[media.above.sm]: {
		// No grid template columns on mobile as hero image will be hidden
		gridTemplateColumns: 'auto 15.5rem',
	},

	[media.above.md]: {
		minHeight: '20rem',
		gridTemplateColumns: 'minmax(20rem, 30rem) 21rem',
	},

	[media.above.lg]: {
		gridTemplateColumns: 'minmax(20rem, 30rem) 24rem',
	},
});

const heroImageContentStyles = css({
	paddingInlineEnd: token('space.300', '24px'),
});

const themedImageStyles = css({
	[media.below.sm]: {
		display: 'none',
	},
});

/**
 * __Hero__
 */
const Hero = ({ headline, description, status, imageSrc, imageDimensions, ...rest }: HeroProps) => {
	return (
		<Container
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={rest.className}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			css={[gutterStyles, imageSrc && heroImageStyles]}
		>
			<Content css={[imageSrc && heroImageContentStyles]}>
				<Stack space="space.200">
					<Stack space="space.100">
						<HeroHeading size="xxxlarge">{headline}</HeroHeading>
						{description && (
							<Text size="large" color="color.text.subtle">
								{description}
							</Text>
						)}
					</Stack>
					{status && <StatusPanel {...status} />}
				</Stack>
			</Content>

			{imageSrc && (
				<ThemedImg
					css={themedImageStyles}
					src={{
						light: imageSrc.light,
						dark: imageSrc.dark,
					}}
					width={imageDimensions && imageDimensions.width}
					height={imageDimensions && imageDimensions.height}
					alt=""
				/>
			)}
		</Container>
	);
};

export default Hero;
